import type { SurveyDraft } from './useSurvey';
import { TRACKED_DEVICE_TYPES, URL_TARGETING_CONDITIONS } from 'definitions';

/*
 * The targeting step's rules, pure so they can be tested without mounting the
 * wizard. Limits mirror the API (schemas.ts: 20 rules, URLs up to 2048).
 */

export type TargetingRule = SurveyDraft['targeting'][number];
export type UrlCondition = (typeof URL_TARGETING_CONDITIONS)[number];
export type TrackedDevice = (typeof TRACKED_DEVICE_TYPES)[number];

export const MAX_RULES = 20;

export const urlRulesOf = (draft: SurveyDraft) => draft.targeting.filter((rule) => rule.condition != null);

export const devicesOf = (draft: SurveyDraft): TrackedDevice[] =>
  TRACKED_DEVICE_TYPES.filter((device) => draft.targeting.some((rule) => rule.device === device));

type WithRulesParams = { urlRules: TargetingRule[]; devices: readonly TrackedDevice[] };

/**
 * The stored shape: one row per URL rule and one per device. No device rows means
 * every device; all three chosen is stored as three rows, which the runtime reads
 * the same way. Collapsing it to none made the third pill clear all three.
 */
export function withRules({ urlRules, devices }: WithRulesParams): TargetingRule[] {
  const deviceRows = devices.map((device) => ({ url: null, condition: null, device }));
  return [...urlRules.map((rule) => ({ ...rule, device: null })), ...deviceRows];
}

/**
 * Whether the rules differ from the saved ones, compared on what they mean. A
 * save sends targeting only when they do: one request fewer on most edits, and one
 * fewer place for a save to half-succeed.
 */
export const targetingChanged = (draft: readonly TargetingRule[], saved: readonly TargetingRule[]): boolean => {
  const canonical = (rules: readonly TargetingRule[]) =>
    JSON.stringify(
      rules
        .map((rule) => [rule.url?.trim() ?? null, rule.condition ?? null, rule.device ?? null])
        .sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))),
    );
  return canonical(draft) !== canonical(saved);
};

/** Rules as saved: URLs trimmed, since " /checkout" would never match a page. */
export const targetingForSave = (draft: SurveyDraft): TargetingRule[] =>
  draft.targeting.map((rule) => ({ ...rule, url: rule.url?.trim() ?? rule.url }));

const compiles = (pattern: string): boolean => {
  try {
    new RegExp(pattern);
    return true;
  } catch {
    return false;
  }
};

/** A filled-in regex rule that does not compile. An empty one is only unfinished. */
export const invalidPattern = (rule: TargetingRule): boolean =>
  rule.condition === 'matches regex' && !!rule.url?.trim() && !compiles(rule.url.trim());

type ExperimentLinkChangedParams = {
  draft: Pick<SurveyDraft, 'experimentId' | 'variantIds'>;
  saved: Pick<SurveyDraft, 'experimentId' | 'variantIds'>;
};

/**
 * Whether the experiment link was edited. Unchanged, it is not sent, because the
 * API checks a link on every write: a survey whose experiment was archived since
 * would otherwise refuse every unrelated edit, such as its theme.
 */
export const experimentLinkChanged = ({ draft, saved }: ExperimentLinkChangedParams): boolean =>
  draft.experimentId !== saved.experimentId ||
  [...draft.variantIds].sort().join() !== [...saved.variantIds].sort().join();

/** What stops the Targeting step, or null. */
export function targetingProblem(draft: SurveyDraft): string | null {
  if (draft.targeting.length > MAX_RULES) return `At most ${MAX_RULES} rules`;
  if (urlRulesOf(draft).some((rule) => !rule.url?.trim())) return 'Fill in or remove the empty page rule';
  if (urlRulesOf(draft).some(invalidPattern)) {
    return 'A page rule pattern is not a valid regular expression';
  }
  if (!Number.isInteger(draft.sampleRate) || draft.sampleRate < 1 || draft.sampleRate > 100) {
    return 'The share of visitors is a whole number from 1 to 100';
  }
  if (draft.variantIds.length > 0 && draft.experimentId === null) return 'Pick the experiment for these variants';
  return null;
}
